import { memo, useMemo } from 'react';
import { Avatar, Card, MetaTable } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { Building2, Calendar, Clock, Mail, Send, Shield } from 'lucide-react';

import { USER_TYPE_META } from '../../constants/copy';
import { SHOW_DEPARTMENT_FIELD } from '../../constants/memberDetail';
import { getAvatarColorClass } from '../../utils/avatarColor';
import { getRelativeDays } from '../../utils/memberDates';
import { getInitials, isPlaceholderProfile } from '../../utils/memberDisplay';
import { getMemberStatusView } from '../../utils/memberStatusView';
import StatusBadge from '../shared/StatusBadge';
import ChangeUserTypeAction from './ChangeUserTypeAction';

/** Icon of each field label (the prototype shows one next to every label). */
const FIELD_ICON = Object.freeze({
  email: Mail,
  joined: Calendar,
  added: Calendar,
  lastActive: Clock,
  invitationSent: Send,
  invitation: Mail,
  department: Building2,
  userType: Shield,
});

/**
 * Left card of the member detail: avatar (initials on a color for a signed-in member, a generic
 * icon for a pending, suspended or nameless one), name (the email local-part when the member has
 * no name), job title, status badge and the labelled fields of the member's state, each value
 * under its label.
 *
 * @param {object} props
 * @param {object} props.member UI member (see `mapApiMemberToUi`)
 * @param {'makeAdmin'|'makeMember'|null} props.userTypeAction From `getMemberActions`.
 * @param {(userType: string) => void} props.onChangeType
 * @param {boolean} [props.showDepartment]
 * @param {import('react').ReactNode} [props.children] Quick actions, under the fields.
 */
function MemberInfoCard({
  member,
  userTypeAction,
  onChangeType,
  showDepartment = SHOW_DEPARTMENT_FIELD,
  children = null,
}) {
  const { t, i18n } = useTranslation('people-access');
  const view = getMemberStatusView(member.status, { showDepartment });

  const items = useMemo(() => {
    const formatDate = (iso) =>
      iso ? new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }).format(new Date(iso)) : '';
    const relative = (iso) => {
      const { key, count } = getRelativeDays(iso);
      return t(key, { count });
    };
    const values = {
      email: <span className="break-all">{member.email}</span>,
      joined: formatDate(member.addedAt),
      added: formatDate(member.addedAt),
      lastActive: relative(member.lastActiveAt),
      invitationSent: relative(member.invitedAt),
      invitation: t('memberDetail.values.notSent'),
      department: member.department,
      userType: (
        <span className="flex items-center justify-between gap-2">
          <span className="text-sm text-content">
            {USER_TYPE_META[member.userType] ? t(USER_TYPE_META[member.userType].labelKey) : ''}
          </span>
          <ChangeUserTypeAction action={userTypeAction} onRequest={onChangeType} />
        </span>
      ),
    };
    return view.fields.map((field) => {
      const Icon = FIELD_ICON[field];
      return {
        key: field,
        label: (
          <span className="flex items-center gap-2">
            {Icon ? <Icon size={14} aria-hidden="true" /> : null}
            {t(`memberDetail.fields.${field}`)}
          </span>
        ),
        value: values[field],
        labelClassName: 'uppercase tracking-wide text-content-muted',
        valueClassName: 'pl-[22px] !text-sm !font-normal !text-content',
      };
    });
  }, [view.fields, member, userTypeAction, onChangeType, t, i18n.language]);

  // A suspended member keeps the initials (CT-5827): only people who never signed in get the generic avatar.
  const hasProfile = !isPlaceholderProfile(member);

  return (
    <Card className="flex flex-col gap-4 p-5" data-testid="member-info-card">
      <div className="flex flex-col items-center gap-2 border-b border-edge pb-4 text-center">
        <Avatar
          size="xxl"
          initials={hasProfile ? getInitials(member.displayName) : undefined}
          placeholder
          className={hasProfile ? getAvatarColorClass(member.displayName) : ''}
          alt={t('memberDetail.avatarAlt', { name: member.displayName })}
        />
        <h2 className="break-all text-base font-semibold text-content">{member.displayName}</h2>
        {member.jobTitle ? <p className="text-xs text-content-muted">{member.jobTitle}</p> : null}
        <StatusBadge status={view.badgeStatus} />
      </div>
      <MetaTable items={items} bordered={false} layout="stacked" divided={false} />
      {children ? <div className="border-t border-edge pt-4">{children}</div> : null}
    </Card>
  );
}

export default memo(MemberInfoCard);
